import React, { useRef, useEffect } from "react";
import { DropDown } from "../../../ui/DropDown";
import { Formik, Field } from "formik";
import Input from "../../../ui/Input";
import { postEndpoint, putEndpoint } from "../../../api/api";
import { COMPANY_URL, PERIOD_URL } from "../../../api/urls";
import { get } from "../../../utils/get";
import DateRange from "../../../ui/DateRange";
import moment from "moment";
import { toast } from "react-toastify";
import { PeriodSchema } from "../../../utils/validationSchema/PeriodSchema";
import { TitleText } from "../../../ui/Text";
import styled from "styled-components";
import FileUploader from "../../../ui/FileUploader";
import { convertJsonToFormData } from "../../../utils/Json2formData";

interface IProps {
  setHandleSubmit?: any;
  closseModal: any;
  editData?: any;
  companies?: any;
}

interface FormValues {
  name: string;
  date_range: { startDate: string | Date; endDate: string | Date };
  grace_period: number;
  close_period_mode: {
    value: string | number;
    label: string;
  };
  notes: string;
  document_name: string;
  contract: any;
}

const AddPeriod = ({
  setHandleSubmit,
  closseModal,
  editData,
  companies,
}: IProps) => {
  const initialValues: FormValues = {
    name: get(editData, "name", ""),
    date_range: {
      startDate: new Date(get(editData, "start_date", new Date())),
      endDate: new Date(get(editData, "end_date", new Date())),
    },
    notes: get(editData, "notes", ""),
    document_name: get(editData, "document_name", ""),
    close_period_mode: {
      label: get(editData, "close_period_mode", ""),
      value: get(editData, "close_period_mode", ""),
    },
    grace_period: get(editData, "grace_period_days", 0),
    contract: "",
  };
  const formRef: any = useRef<any>(null);
  const [createRes, createReq] = postEndpoint();
  const [updateRes, updateReq] = putEndpoint();

  const { id } = editData;

  const isAddMode = !id;

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  useEffect(() => {
    setHandleSubmit(() => handleSubmit);
    return () => {};
  }, [setHandleSubmit]);

  const addPeriod = (values: FormValues) => {
    const payload = {
      name: values.name,
      start_date: moment(values.date_range.startDate).format("YYYY-MM-DD"),
      end_date: moment(values.date_range.endDate).format("YYYY-MM-DD"),
      notes: values.notes,
      grace_period_days: values.grace_period,
      close_period_mode: values.close_period_mode.value,
      document_name: values.document_name,
      document: values.contract,
    };
    const formData = convertJsonToFormData(payload);
    createReq(PERIOD_URL, formData);
  };

  const editPeriod = (values: FormValues) => {
    const payload = {
      name: values.name,
      start_date: moment(values.date_range.startDate).format("YYYY-MM-DD"),
      end_date: moment(values.date_range.endDate).format("YYYY-MM-DD"),
      notes: values.notes,
      grace_period_days: values.grace_period,
      close_period_mode: values.close_period_mode.value,
      document_name: values.document_name,
      document: values.contract,
    };
    const formData = convertJsonToFormData(payload);
    updateReq(`${PERIOD_URL}/${id}`, formData);
  };

  useEffect(() => {
    const { complete, pending, data, error } = createRes;
    if (error) {
      toast.error(get(data, "message"));
    }
    if (!pending && complete && !error) {
      closseModal();
      toast.success("Period configured successfully");
    }
  }, [closseModal, companies, createRes]);

  useEffect(() => {
    const { complete, pending, data } = updateRes;
    if (!pending && complete && data) {
      closseModal();
    }
  }, [closseModal, companies, updateRes]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={PeriodSchema}
      innerRef={formRef}
      onSubmit={
        isAddMode
          ? (values) => addPeriod(values)
          : (values) => editPeriod(values)
      }
    >
      {() => (
        <>
          <Field name="name" component={Input} label="Name" />
          <Field
            name="date_range"
            label="Start and end date"
            component={DateRange}
          />
          <Field name="grace_period" component={Input} label="Grace period" />
          <Field
            name="close_period_mode"
            component={DropDown}
            options={[
              { label: "auto", value: "auto" },
              { label: "manual", value: "manual" },
            ]}
            isBordered={true}
            dropdownLabel="Close Period Mode"
            width="96%"
          />
          <TitleText style={{ margin: "1em 0em" }}>
            Performance Contract
          </TitleText>
          <Field
            name="document_name"
            type="text"
            label="Document Name"
            component={Input}
            // width="90%"
            isBordered={true}
            placeholder="Name"
          />
          <Label>
            <Field
              type="file"
              name="contract"
              component={FileUploader}
              accept=".pdf"
              label="Performance contract"
              backgroundColor="#F5F6FB"
              width="90%"
            />
          </Label>

          <Field
            name="notes"
            component={Input}
            label="Notes"
            type="textarea"
            height="6em"
          />
        </>
      )}
    </Formik>
  );
};

export default AddPeriod;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  color: #01A8CE;
  font-family: "Raleway", sans-serif;
  font-size: 0.8em;
  margin: 0.5em 0;
  position: relative;
`;
